import { useState } from 'react';
import { useSpeechProviders } from '@/hooks/useSpeechProviders';
import { SpeechProviderDialog } from '@/components/superadmin/SpeechProviderDialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import {
  NO_ACTIVE_SPEECH_BANNER, canActivateSpeech, describeSpeechError, describeSpeechTest, speechActivateBlockedReason,
  speechHealthText, type SpeechProvider,
} from '@/lib/speechProviderLogic';

const HEALTH_STYLE: Record<string, string> = {
  healthy: 'text-success',
  unhealthy: 'text-destructive',
  unknown: 'text-muted-foreground',
};

type Confirm = { kind: 'activate' | 'deactivate' | 'delete'; provider: SpeechProvider } | null;

export function SpeechSection() {
  const hook = useSpeechProviders();
  const { providers, loading, error, testProvider, setActive, deactivate, deleteProvider, refresh } = hook;
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<SpeechProvider | undefined>();
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [lastConfirm, setLastConfirm] = useState<NonNullable<Confirm> | null>(null);
  const [busy, setBusy] = useState(false);
  const [rowBusy, setRowBusy] = useState<string | null>(null);
  const shownConfirm = confirm ?? lastConfirm;
  const anyBusy = busy || rowBusy !== null;
  const askConfirm = (c: NonNullable<Confirm>) => { setLastConfirm(c); setConfirm(c); };
  const hasActive = providers.some((p) => p.is_active);

  const fail = (title: string, e: unknown) =>
    toast({ title, description: describeSpeechError((e as { code?: string }).code, (e as Error).message), variant: 'destructive' });

  const runTest = async (p: SpeechProvider) => {
    setRowBusy(p.id);
    try {
      const r = await testProvider({ id: p.id, baseUrl: p.base_url, defaultModel: p.default_model, speechConfig: p.speech_config ?? {} });
      const d = describeSpeechTest(r);
      toast({ title: d.title, description: d.detail, variant: d.ok ? undefined : 'destructive' });
      await refresh({ quiet: true });
    } catch (e) { fail('Test failed', e); } finally { setRowBusy(null); }
  };

  const runConfirm = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      if (confirm.kind === 'activate') {
        await setActive(confirm.provider.id);
        toast({ title: `${confirm.provider.label} is now the active speech provider` });
      } else if (confirm.kind === 'deactivate') {
        await deactivate(confirm.provider.id);
        toast({ title: 'Speech provider deactivated', description: 'Ask Masira uses on-device voices.' });
      } else {
        await deleteProvider(confirm.provider.id);
        toast({ title: `Deleted ${confirm.provider.label}` });
      }
      setConfirm(null);
    } catch (e) {
      fail(confirm.kind === 'delete' ? 'Could not delete' : confirm.kind === 'activate' ? 'Could not activate' : 'Could not deactivate', e);
      setConfirm(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-labelledby="speech-heading" className="space-y-4 border-t pt-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 id="speech-heading" className="text-xl font-semibold">Speech</h2>
          <p className="text-sm text-muted-foreground">
            Text-to-speech for Ask Masira spoken replies ("cloud voice"). Tenant admins opt in under Tenant Settings; replies fall back to on-device voices on any failure.
          </p>
        </div>
        <Button onClick={() => { setEditing(undefined); setDialogOpen(true); }}>Add speech provider</Button>
      </div>

      {!hasActive && !loading && !error && (
        <div role="status" className="rounded-md border border-warning/50 bg-warning/10 p-3 text-sm">{NO_ACTIVE_SPEECH_BANNER}</div>
      )}
      {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}

      {!loading && !error && providers.length === 0 && (
        <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          No speech providers configured. Add one (for example Groq) to give Ask Masira a consistent voice, including Arabic.
        </div>
      )}

      {providers.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <caption className="sr-only">Speech providers</caption>
            <thead className="text-start text-muted-foreground">
              <tr>
                <th scope="col" className="py-2 text-start">Label</th><th scope="col" className="text-start">Base URL</th>
                <th scope="col" className="text-start">Languages</th><th scope="col" className="text-start">Key</th>
                <th scope="col" className="text-start">Health</th><th scope="col" className="text-start">Status</th>
                <th scope="col"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {providers.map((p) => (
                <tr key={p.id} className="border-t">
                  <td className="py-2">{p.label}</td>
                  <td className="font-mono text-xs break-all">{p.base_url}</td>
                  <td className="text-xs">{(p.speech_config && typeof p.speech_config === 'object' ? Object.keys(p.speech_config).join(', ') : '') || '—'}</td>
                  <td className="font-mono text-xs">{p.api_key_last4 ? `•••• ${p.api_key_last4}` : '—'}</td>
                  <td className={HEALTH_STYLE[p.health_status]} title={p.health_error ?? ''}>{speechHealthText(p)}</td>
                  <td>
                    {p.is_active
                      ? <span className="rounded bg-success/15 px-2 py-0.5 text-xs font-medium text-success">Active</span>
                      : <span className="text-muted-foreground">inactive</span>}
                  </td>
                  <td className="space-x-3 text-end whitespace-nowrap">
                    {p.is_active ? (
                      <button className="text-primary underline disabled:opacity-50 disabled:no-underline" disabled={anyBusy}
                        aria-label={`Deactivate ${p.label}`} onClick={() => askConfirm({ kind: 'deactivate', provider: p })}>deactivate</button>
                    ) : (
                      <button className="text-primary underline disabled:opacity-50 disabled:no-underline"
                        disabled={anyBusy || !canActivateSpeech(p)} aria-label={`Activate ${p.label}`}
                        title={speechActivateBlockedReason(p) ?? undefined}
                        onClick={() => askConfirm({ kind: 'activate', provider: p })}>activate</button>
                    )}
                    <button className="text-primary underline disabled:opacity-50 disabled:no-underline" disabled={anyBusy}
                      aria-label={`Test ${p.label}`} onClick={() => void runTest(p)}>
                      {rowBusy === p.id ? 'testing…' : 'test'}
                    </button>
                    <button className="text-primary underline disabled:opacity-50 disabled:no-underline" disabled={anyBusy}
                      aria-label={`Edit ${p.label}`} onClick={() => { setEditing(p); setDialogOpen(true); }}>edit</button>
                    <button className="text-destructive underline disabled:opacity-50 disabled:no-underline"
                      disabled={anyBusy || p.is_active} aria-label={`Delete ${p.label}`}
                      title={p.is_active ? 'The active provider cannot be deleted. Deactivate it first.' : undefined}
                      onClick={() => askConfirm({ kind: 'delete', provider: p })}>delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SpeechProviderDialog open={dialogOpen} onOpenChange={setDialogOpen} provider={editing} actions={hook} />

      <AlertDialog open={!!confirm} onOpenChange={(o) => { if (!o && !busy) setConfirm(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {shownConfirm?.kind === 'activate' && `Activate ${shownConfirm.provider.label}?`}
              {shownConfirm?.kind === 'deactivate' && `Deactivate ${shownConfirm.provider.label}?`}
              {shownConfirm?.kind === 'delete' && `Delete ${shownConfirm.provider.label}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {shownConfirm?.kind === 'activate' && `Ask Masira will send reply text to ${shownConfirm.provider.label} to speak it, for tenants that have turned on Cloud voice.`}
              {shownConfirm?.kind === 'deactivate' && 'Cloud voice stops for every tenant; Ask Masira reads replies with on-device voices.'}
              {shownConfirm?.kind === 'delete' && 'This removes the provider and its stored key. This cannot be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={busy} onClick={(e) => { e.preventDefault(); void runConfirm(); }}>
              {shownConfirm?.kind === 'activate' ? 'Activate' : shownConfirm?.kind === 'deactivate' ? 'Deactivate' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
